import { clampStonePattern } from "../../shared/api-contract";
import type { DiamondSetting, DiamondStyle, EngravingConfig, RingState, StoneType } from "../../shared/api-contract";

/**
 * Legacy-id migration for a restored design.
 *
 * WHY THIS EXISTS. A saved design is an opaque blob — `?design=z:<deflate>` in
 * the URL, or a JSON row in the Vault — with no schema version anywhere. Every
 * lookup in the engine then degrades GRACEFULLY rather than failing loudly: an
 * unknown `DiamondStyle` resolves to `none` (BandConfig.ts), an unknown
 * `finishId` falls back to polished (metalPresets.ts), an unknown stone shape
 * falls back to the first bare cut. That is the right runtime behaviour and the
 * wrong migration story — together they turn "we retired an id" into "your ring
 * silently lost its stones", with no signal at any layer.
 *
 * So retiring an id is never a one-line delete. It is a delete PLUS an entry
 * here, and the check in scripts/check-id-migration.ts fails the build if the
 * two come apart: it replays a frozen fixture of every id that was ever valid
 * and asserts each one still lands somewhere the current contract knows.
 *
 * The maps are empty today, deliberately — nothing has been retired yet. The
 * fixture was captured at exactly this moment, BEFORE the Gem-tab wave, which
 * is the only point at which "every id that was ever valid" can be recorded
 * honestly.
 *
 * SCOPE. This covers the `z:` URL blob, which cannot be rewritten in place —
 * migration has to happen on read, every time. Vault designs are plain
 * server-side JSON and could additionally be batch-migrated once; this module
 * is the map such a job would import.
 */

export interface MigrationNote {
  /** Dotted path into the RingState, e.g. `diamond.style`. */
  path: string;
  from: string;
  to: string;
}

/**
 * Retired `DiamondStyle` ids → the surviving id that best preserves the look.
 *
 * NOT a place to drop an id you merely stopped showing in the UI: hiding a tile
 * keeps old designs rendering, and that is always preferable. Only an id that
 * has genuinely left the contract union belongs here.
 */
export const STYLE_ALIASES: Readonly<Record<string, DiamondStyle>> = Object.freeze({});

/** Retired `DiamondSetting` ids → survivor. */
export const SETTING_ALIASES: Readonly<Record<string, DiamondSetting>> = Object.freeze({});

/** Retired `StoneType` ids → survivor. Item 16 (remove gold bead) lands here. */
export const STONE_TYPE_ALIASES: Readonly<Record<string, StoneType>> = Object.freeze({});

/**
 * Retired metal finish ids → survivor.
 *
 * `finishId` is a free `string` in the contract, resolved against the viewer's
 * METAL_FINISHES catalogue, and an unknown one degrades to polished with no
 * error — so replacing the finish list without filling this in changes the
 * appearance of every design that used a dropped finish, silently.
 */
export const FINISH_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  // v52 (item 5) — the finish list is now the reference's seven exactly, so six
  // ids left the catalogue. Each maps to the survivor closest in character, and
  // this map is the ONLY thing standing between a saved design and a silent
  // change of appearance: `createMetalMaterial` answers an unknown finish with
  // POLISHED, so without these every one of them would become a mirror.
  satin: "brushed",        // both are a fine directional sheen
  matte: "sand",           // the diffuse, un-reflective one
  sandblast: "sand",       // same family, coarser
  florentine: "linear",    // fine parallel engraving
  wave: "hammered",        // undulating relief
  mirror: "polished",      // polished IS the mirror; `mirror` only added a coat
  // "milgrain" was a procedural normal map calling itself Milgrain — one of four
  // unrelated things with that name. Nothing in the new set is a bead texture,
  // and the closest surface is the fine sheen.
  milgrain: "brushed",
});

/** Retired stone-shape ids → survivor. Open vocabulary (StoneShape = string),
 *  so this is checked against the shipped GLB catalogue, not a union. */
export const STONE_SHAPE_ALIASES: Readonly<Record<string, string>> = Object.freeze({});

const remap = <T extends string>(
  value: T | undefined,
  table: Readonly<Record<string, string>>,
  path: string,
  notes: MigrationNote[],
): T | undefined => {
  if (value == null) return value;
  const to = table[value];
  if (to == null || to === value) return value;
  notes.push({ path, from: value, to });
  return to as T;
};

/**
 * Rewrite every retired id in a restored design.
 *
 * Pure: returns a new state and never mutates the input, so undo/redo history
 * and the token the design was decoded from stay untouched. `notes` is the
 * audit trail — empty means nothing was migrated, which is the normal case.
 */
export function migrateRingState(s: RingState): { state: RingState; notes: MigrationNote[] } {
  const notes: MigrationNote[] = [];

  const diamond = {
    ...s.diamond,
    style: remap(s.diamond.style, STYLE_ALIASES, "diamond.style", notes)!,
    setting: remap(s.diamond.setting, SETTING_ALIASES, "diamond.setting", notes),
    type: remap(s.diamond.type, STONE_TYPE_ALIASES, "diamond.type", notes),
    stoneShape: remap(s.diamond.stoneShape, STONE_SHAPE_ALIASES, "diamond.stoneShape", notes)!,
  };

  const materials = s.materials.map((m, i) => ({
    ...m,
    finishId: remap(m.finishId, FINISH_ALIASES, `materials[${i}].finishId`, notes)!,
  }));

  // Every free stone carries its OWN optional setting and type, resolved
  // per-stone in `freeRun` — so a retirement has to be applied here too, not
  // only to the run-level defaults above.
  const freeStones = s.freeStones?.map((f, i) => ({
    ...f,
    type: remap(f.type, STONE_TYPE_ALIASES, `freeStones[${i}].type`, notes),
    setting: remap(f.setting, SETTING_ALIASES, `freeStones[${i}].setting`, notes),
  }));

  // v67 blocks (band designs W2–W5). Every other new field — `paramProfile`,
  // `DesignGroove.vAngleDeg` / the partial-step preset, the layout — rides
  // the spread untouched: it is plain data the viewer clamps itself, and
  // nothing in it has been retired. Two are normalised here because a token
  // is untrusted input that outlives the code that wrote it:
  //   · `stonePattern` through the contract's own `clampStonePattern` (what
  //     the engine stores), so a hand-edited or older token cannot carry a
  //     layer list the generator would refuse — and an EMPTY one is dropped
  //     rather than restored as a pattern with no stones;
  //   · `engraving.logo` with neither an `svg` nor a `url` is dropped (it would
  //     only be refused by the viewer's sanitiser).
  const stonePattern = s.stonePattern !== undefined ? clampStonePattern(s.stonePattern) : undefined;
  const engraving = migrateEngraving(s.engraving, notes);

  const state: RingState = { ...s, diamond, materials, ...(freeStones ? { freeStones } : {}) };
  if (s.stonePattern !== undefined) {
    if (stonePattern) state.stonePattern = stonePattern;
    else delete state.stonePattern;
  }
  if (engraving !== s.engraving) state.engraving = engraving;
  return { state, notes };
}

function migrateEngraving(e: EngravingConfig, notes: MigrationNote[]): EngravingConfig {
  const logo = e?.logo;
  if (logo == null) return e;
  if (typeof logo !== "object" || (typeof logo.svg !== "string" && typeof logo.url !== "string")) {
    const { logo: _drop, ...rest } = e;
    return rest as EngravingConfig;
  }
  // A token is untrusted and its logo url is FETCHED by everyone who opens
  // the link (review finding: any url, a LAN address included, rode a share
  // link). Only a picture the Vault serves, this app's own origin, or an
  // inline data: picture is kept; anything else is dropped (the svg stays).
  if (typeof logo.url === "string" && !isTrustedLogoUrl(logo.url)) {
    notes.push({ path: "engraving.logo.url", from: logo.url.slice(0, 80), to: "(dropped: not a Vault or inline picture)" });
    const { url: _url, ...kept } = logo;
    if (typeof kept.svg === "string") return { ...e, logo: kept };
    const { logo: _drop, ...rest } = e;
    return rest as EngravingConfig;
  }
  return e;
}

/** Hosts a Vault asset is served from: a workspace's own CloudFront / S3, or ours. */
const LOGO_HOSTS = [/\.cloudfront\.net$/i, /\.amazonaws\.com$/i, /(^|\.)jewelitt\.com$/i];

/** A logo url the host lets a restored design keep (see `migrateEngraving`). */
export function isTrustedLogoUrl(url: string): boolean {
  const u = url.trim();
  if (/^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(u)) return true;
  if (/^\/(?!\/)/.test(u)) return true; // this app's own origin
  let parsed: URL;
  try { parsed = new URL(u); } catch { return false; }
  if (typeof location !== "undefined" && parsed.origin === location.origin) return true;
  return parsed.protocol === "https:" && !parsed.username && !parsed.password && LOGO_HOSTS.some((re) => re.test(parsed.hostname));
}
